/**
 * Pick one locale's title out of a map of locale → text.
 *
 * A catalogue is knowledge: it carries every locale the deployment supports and
 * makes no locale decision of its own, so the reader picks. The walk is the UI
 * language, then its base ("de-CH" → "de"), then English, then the first value
 * present — and it treats a blank string as absent, because an empty title is
 * not a name.
 *
 * The last step is the one worth explaining. A French title shown to a German
 * reader is real data, mislabelled; a blank label is a silent failure she
 * cannot act on, because there is nothing on screen to report. So a map that
 * carries only French renders French rather than nothing. What this walk does
 * NOT do is prefer a particular language over the reader's own: an earlier
 * version of this code hard-coded German ahead of English, which quietly served
 * German to a French ward.
 *
 * This is the ONE place that walk is written. It answers for a skill
 * catalogue's `title`, a team-skill row's and an extract's `terms`, because all
 * three are the same wire shape asking the same question. `title` is optional so a caller holding a block the server omits
 * hands it over as it is rather than guarding first.
 *
 * Returns undefined only when the map carries NO usable title at all. That is
 * deliberate: what to show instead is the caller's answer, and it differs by
 * resource — a catalogue skill falls back to its id, a team-skill row to the id
 * inside its reference. Substituting a placeholder
 * here would take that choice away and put an empty string on screen.
 */
/**
 * A map whose type GUARANTEES a non-empty English title always answers, because
 * `en` is the third step of the walk. Stating that in the signature is what
 * lets a caller holding such a map read the result directly instead of writing
 * a fallback that can never run — and a fallback that can never run reads, to
 * the next person, as a case that can.
 */
export function localisedTitle(
  title: { en: string } & Record<string, string>,
  language: string,
): string;
export function localisedTitle(
  title: Record<string, string> | undefined,
  language: string,
): string | undefined;
export function localisedTitle(
  title: Record<string, string> | undefined,
  language: string,
): string | undefined {
  if (!title) return undefined;
  for (const key of [language, language.split('-')[0], 'en']) {
    const value = key ? title[key] : undefined;
    if (value && value.trim() !== '') return value;
  }
  // The reader's languages are exhausted. Anything present beats nothing.
  for (const value of Object.values(title)) {
    if (value && value.trim() !== '') return value;
  }
  return undefined;
}
